import React from 'react';
import type { OnboardingAnswers, ComplianceFramework, BYOKProvider } from '../../../../shared/types/accounts';

interface Props {
  needs: OnboardingAnswers['needs'];
  onChange: (needs: OnboardingAnswers['needs']) => void;
}

function Checkbox({ checked, onChange, label, description }: {
  checked: boolean; onChange: (v: boolean) => void; label: string; description?: string;
}) {
  return (
    <label className="flex items-start gap-3 cursor-pointer group">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-primary-600"
      />
      <div>
        <span className="text-sm font-medium text-slate-800">{label}</span>
        {description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
      </div>
    </label>
  );
}

function toggleArr<T>(arr: T[], val: T): T[] {
  return arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val];
}

const COMPLIANCE_OPTIONS: Array<{ value: ComplianceFramework; label: string }> = [
  { value: 'hipaa', label: 'HIPAA' },
  { value: 'hitrust', label: 'HITRUST' },
  { value: 'soc2', label: 'SOC 2' },
  { value: 'fedramp', label: 'FedRAMP' },
  { value: 'pci', label: 'PCI DSS' },
];

const PROVIDER_OPTIONS: Array<{ value: BYOKProvider; label: string }> = [
  { value: 'bedrock', label: 'AWS Bedrock' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'vertex', label: 'Google Vertex AI' },
];

/**
 * UX-08: body-only — the checkbox/pill groups from the old `StepNeeds`
 * step, minus its own Back/Continue footer.
 */
export const NeedsFields: React.FC<Props> = ({ needs, onChange }) => (
  <div className="space-y-5">
    <div className="space-y-3">
      <p className="text-xs font-medium text-slate-700">Deployment features</p>
      <Checkbox
        checked={needs.testDeploys}
        onChange={(v) => onChange({ ...needs, testDeploys: v })}
        label="Test environment deploys"
        description="Deploy to a staging environment and run smoke tests before production"
      />
      <Checkbox
        checked={needs.browserSmoke}
        onChange={(v) => onChange({ ...needs, browserSmoke: v })}
        label="Browser smoke tests"
        description="Run Playwright-based UI tests against deployed environments"
      />
    </div>

    <div>
      <p className="text-xs font-medium text-slate-700 mb-2">Compliance frameworks</p>
      <div className="flex flex-wrap gap-2">
        {COMPLIANCE_OPTIONS.map(({ value, label }) => {
          const selected = needs.compliance.includes(value);
          return (
            <button
              key={value}
              type="button"
              onClick={() => onChange({ ...needs, compliance: toggleArr(needs.compliance, value) })}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                selected ? 'bg-primary-600 border-primary-600 text-white' : 'bg-white border-slate-300 text-slate-700 hover:border-slate-400'
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>

    <div>
      <p className="text-xs font-medium text-slate-700 mb-2">LLM providers beyond Anthropic</p>
      <div className="flex flex-wrap gap-2">
        {PROVIDER_OPTIONS.map(({ value, label }) => {
          const selected = needs.providers.includes(value);
          return (
            <button
              key={value}
              type="button"
              onClick={() => onChange({ ...needs, providers: toggleArr(needs.providers, value) })}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                selected ? 'bg-primary-600 border-primary-600 text-white' : 'bg-white border-slate-300 text-slate-700 hover:border-slate-400'
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>

    <Checkbox
      checked={needs.ssoScim}
      onChange={(v) => onChange({ ...needs, ssoScim: v })}
      label="SSO / SCIM provisioning"
      description="SAML/OIDC single sign-on and automated user provisioning"
    />
  </div>
);
